import { useState } from 'react'
import { Button } from '@ascleo/ui/components/ui/button'
import { useTranslation } from 'react-i18next'
import { Building2, Check, CircleCheck, Pencil, User, UserRound } from 'lucide-react'
import { useCheckInSend, useCheckInSelector } from '../context'
import { checkInSelectors } from '../selectors'
import type { CardData } from '../machine'
import type { Gender } from '../schema'
import { useAudio } from '../../../../../lib/use-audio'
import { kioskOperationsClient } from '../../../../../lib/rpc/kiosk-operations.client'
import { frontdeskDiagnosticLogger } from '../../../../../lib/diagnostic-logger'
import { KioskStep, KioskStepColumns } from '../../../../../components/kiosk-step'
import { KioskTextField } from '../../../../../components/kiosk-field'
import { KioskDateOfBirthField } from '../../../../../components/kiosk-date-of-birth-field'
import { KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import {
  dateOfBirthToDate,
  formatDateOfBirthForDisplay,
  normalizeDateOfBirthForApi,
} from '../../../components/DateOfBirthInput'
import { CheckInChoiceGroup } from './check-in-choice-group'
import { cardGenderToCode, GENDER_CODES } from './registration-display'
import { RegistrationSummary } from './registration-summary'

function mapGender(gender: Gender): 'MALE' | 'FEMALE' | 'VARIOUS' | 'UNKNOWN' {
  switch (gender) {
    case 'M':
      return 'MALE'
    case 'W':
      return 'FEMALE'
    case 'D':
      return 'VARIOUS'
    case 'U':
      return 'UNKNOWN'
  }
}

function mapTalk(gender: Gender): 'MR' | 'MS' | 'NONE' {
  switch (gender) {
    case 'M':
      return 'MR'
    case 'W':
      return 'MS'
    default:
      return 'NONE'
  }
}

interface CardRegistrationForm {
  givenName: string
  familyName: string
  birthDate: string
  gender: Gender
}

function initialForm(cardData: CardData | undefined): CardRegistrationForm {
  return {
    givenName: cardData?.givenName ?? '',
    familyName: cardData?.familyName ?? '',
    // Already decoded to YYYY-MM-DD (or "") at the card ingress (KC-74).
    birthDate: cardData?.birthDate ?? '',
    gender: cardData ? cardGenderToCode(cardData.gender) : 'U',
  }
}

function isFormComplete(form: CardRegistrationForm): boolean {
  return (
    form.givenName.trim() !== '' &&
    form.familyName.trim() !== '' &&
    dateOfBirthToDate(form.birthDate) !== undefined
  )
}

/**
 * Register new patient from card data.
 *
 * Shows the decoded card data in kiosk sizes; "Ändern" opens the same fields
 * as the manual registration in place. The footer action always registers the
 * current values, so an open editor never blocks it (KC-79).
 *
 * @since 0.1.0
 * @example
 * ```tsx
 * <RegisterFromCardStep />
 * ```
 */
export function RegisterFromCardStep() {
  const { t } = useTranslation()
  const send = useCheckInSend()
  const cardData = useCheckInSelector(checkInSelectors.cardData)
  const egkAttestation = useCheckInSelector(checkInSelectors.egkAttestation)

  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string>()
  const [formData, setFormData] = useState(() => initialForm(cardData))
  // A card without a readable birth date opens the editor right away.
  const [isEditing, setIsEditing] = useState(() => !isFormComplete(initialForm(cardData)))

  useAudio('checkIn-registerFromCard')

  const handleCreate = async () => {
    if (!cardData) {
      setError(t('self-service.check-in.register-from-card.no-card-data'))
      return
    }

    if (!cardData.insurance.insurerId) {
      // The card carries no cost-carrier IK, so a retry cannot succeed: say so and point to reception.
      setError(t('self-service.check-in.register-from-card.insurer-missing-v2'))
      return
    }

    setIsCreating(true)
    setError(undefined)

    try {
      const outcome = await kioskOperationsClient.createPatient({
        firstname: formData.givenName.trim(),
        lastname: formData.familyName.trim(),
        dob: normalizeDateOfBirthForApi(formData.birthDate),
        gender: mapGender(formData.gender),
        talk: mapTalk(formData.gender),
        insurance: 'GKV', // Card-based registration is always GKV
        insurerId: cardData.insurance.insurerId,
        // Only the card-registration path supplies the attestation; a successful create commits it so
        // the new patient becomes eligible for the review. Manual registration omits it.
        ...(egkAttestation === undefined ? {} : { egkAttestation })
      })

      if (outcome.kind === 'policy-restricted') {
        send({ type: 'PATIENT_CREATION_RESTRICTED' })
        return
      }

      if (outcome.kind === 'failed') {
        frontdeskDiagnosticLogger.error('Kiosk card patient creation failed', {
          outcome: 'creation-failed',
          insurance: 'GKV'
        })
        setError(t('self-service.check-in.register-from-card.create-error-v2'))
        return
      }

      send({ type: 'CREATE_FROM_CARD_OK', patientId: outcome.patientId })
    } catch {
      frontdeskDiagnosticLogger.error('Kiosk card patient creation failed', {
        outcome: 'client-defect',
        insurance: 'GKV'
      })
      setError(t('self-service.check-in.register-from-card.create-error-v2'))
    } finally {
      setIsCreating(false)
    }
  }

  const receptionHelp = {
    label: t('self-service.help-reception'),
    icon: Building2,
    onClick: () => send({ type: 'RECEPTION_HELP' }),
    testId: 'frontdesk-register-from-card-reception-btn',
  }

  if (!cardData) {
    return (
      <KioskStep
        width="narrow"
        align="center"
        title={t('self-service.check-in.register-from-card.no-card-data-title')}
        primaryAction={receptionHelp}
      >
        <KioskStatePanel
          tone="error"
          title={t('self-service.check-in.register-from-card.no-card-data-title')}
          body={t('self-service.check-in.register-from-card.no-card-data')}
        />
      </KioskStep>
    )
  }

  const genderOptions = GENDER_CODES.map((code) => ({
    value: code,
    label: t(`common.gender.${code}`),
    testId: `frontdesk-register-from-card-gender-${code.toLowerCase()}-btn`,
  }))

  return (
    <KioskStep
      width="default"
      title={t('self-service.check-in.register-from-card.title-v2')}
      lead={t('self-service.check-in.register-from-card.subtitle-v2')}
      primaryAction={{
        label: t('self-service.check-in.register-from-card.create-v2'),
        icon: Check,
        onClick: () => void handleCreate(),
        disabled: !isFormComplete(formData),
        busy: isCreating,
        testId: 'register-from-card-create',
      }}
      secondaryAction={error === undefined ? undefined : receptionHelp}
    >
      <KioskStepColumns
        aside={
          <>
            <p className="m-0 flex items-start gap-3 rounded-2xl border border-success-subtle bg-success-soft p-5 text-lg text-foreground">
              <CircleCheck aria-hidden className="mt-0.5 size-6 shrink-0 text-success-ink" />
              {t('self-service.check-in.register-from-card.insurance-verified', {
                insurer: cardData.insurance.insuranceName
              })}
            </p>
            {error ? (
              <KioskStatePanel
                tone="error"
                title={error}
                testId="frontdesk-register-from-card-error"
              />
            ) : null}
          </>
        }
      >
        <section className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="m-0 text-xl font-semibold">
              {t('self-service.check-in.register-from-card.card-data')}
            </h2>
            <Button
              data-testid="frontdesk-register-from-card-edit-btn"
              type="button"
              variant="outline"
              size="2xl"
              aria-expanded={isEditing}
              onClick={() => setIsEditing((current) => !current)}
            >
              {isEditing ? (
                <Check aria-hidden className="size-6" />
              ) : (
                <Pencil aria-hidden className="size-6" />
              )}
              {isEditing
                ? t('common.done')
                : t('self-service.check-in.register-from-card.edit')}
            </Button>
          </div>

          {isEditing ? (
            <div className="flex flex-col gap-5">
              <KioskTextField
                id="checkin-card-register-given-name"
                testId="frontdesk-register-from-card-given-name-input"
                label={t('self-service.check-in.register-basics.given-name')}
                icon={User}
                required
                autoComplete="given-name"
                value={formData.givenName}
                onChange={(givenName) => setFormData((prev) => ({ ...prev, givenName }))}
              />
              <KioskTextField
                id="checkin-card-register-family-name"
                testId="frontdesk-register-from-card-family-name-input"
                label={t('self-service.check-in.register-basics.family-name')}
                icon={UserRound}
                required
                autoComplete="family-name"
                value={formData.familyName}
                onChange={(familyName) => setFormData((prev) => ({ ...prev, familyName }))}
              />
              <KioskDateOfBirthField
                id="checkin-card-register-dob"
                testId="frontdesk-register-from-card-birth-date-input"
                pickerTestId="frontdesk-register-from-card-birth-date-picker-btn"
                label={t('self-service.check-in.register-basics.dob')}
                description={t('self-service.check-in.dob-format-hint')}
                required
                value={formData.birthDate}
                onChange={(birthDate) => setFormData((prev) => ({ ...prev, birthDate }))}
                error={
                  dateOfBirthToDate(formData.birthDate) === undefined
                    ? t('self-service.check-in.dob-required')
                    : undefined
                }
              />
              <CheckInChoiceGroup
                legend={t('self-service.check-in.register-basics.gender')}
                options={genderOptions}
                value={formData.gender}
                onChange={(gender) => setFormData((prev) => ({ ...prev, gender }))}
              />
            </div>
          ) : (
            <RegistrationSummary
              testId="frontdesk-register-from-card-summary"
              rows={[
                {
                  label: t('self-service.check-in.register-from-card.name'),
                  value: `${formData.givenName} ${formData.familyName}`.trim(),
                },
                {
                  label: t('self-service.check-in.register-basics.dob'),
                  value: formatDateOfBirthForDisplay(formData.birthDate),
                },
                {
                  label: t('self-service.check-in.register-basics.gender'),
                  value: t(`common.gender.${formData.gender}`),
                },
                {
                  label: t('self-service.check-in.register-from-card.insurer'),
                  value: cardData.insurance.insuranceName,
                },
              ]}
            />
          )}
        </section>
      </KioskStepColumns>
    </KioskStep>
  )
}
